import createMuiTheme from '@material-ui/core/styles/createTheme';

const defaultBorder = (color = '#c6c0b3') => `.1rem solid ${color}`;
const defaultBorderRadius = '4px';
const defaultBoxShadow = '0 0 .5rem 0 rgba(0,0,0,.1)';
const defaultTransition = 'all .25s linear';
const primaryFontFamily = ['myriad-pro', 'Ubuntu', 'Times New Roman', 'sans-seri', 'Poppins'].join(
  ','
);
const secondaryFontFamily = ['Lato', 'Arial', 'sans-serif'].join(',');
const borderRadiusCluster = 20;
const trajectoryToolboxWidth = 60;
const appbarHeight = 60;
const subHeaderHeight = 50;
const appbarHeightPx = `${appbarHeight}px`;
const subHeaderHeightPx = `${subHeaderHeight}px`;
const bottomBarHeight = 60;
const bottomBarHeightPx = `${bottomBarHeight}px`;
const messageBarHeight = 50;
const mobileMessageBarHeight = 60;
const messageBarHeightPx = `${messageBarHeight}px`;
const sidePanelScroolWidthRem = '0.5rem';
const closedNavigationBarWidth = 48;
const closedNavigationBarWidthPx = `${closedNavigationBarWidth}px`;
const openNavigationBarWidth = 280;
const openNavigationBarWidthPx = `${openNavigationBarWidth}px`;

const theme = createMuiTheme({
  name: 'DigitalMethodeTheme',
  palette: {
    primary: {
      main: '#EC6523',
      dark: '#BA501C',
    },
    secondary: {
      main: '#7e7664',
    },
    ternary: {
      main: '#0092BC',
      dark: '#006B84',
    },
    text: {
      primary: '#2e2a28',
    },

    grey: {
      50: '#fefaf1',
      100: '#f1ece2',
      200: '#e4dfd5',
      300: '#d6d0c4',
      400: '#c6c0b3',
      500: '#b6b0a2',
      600: '#a7a091',
      700: '#989181',
      800: '#8b8473',
      900: '#7e7664',
      A100: '#6c6453',
      A200: '#595243',
      A400: '#443f33',
      A700: '#2f2b29',
    },
    background: {
      default: '#FAFAFA',
    },
    offblack: '#2e2a28',
    error: {
      light: '#ffe0e0',
      main: '#f00',
      dark: '#ffc7c7',
    },
    info: {
      light: '#E0F0F6',
      main: '#116A7E',
      dark: '#0b4a58',
    },
    warning: {
      light: '#f9f0bd',
      main: '#EBCD25',
      dark: '#eba406',
    },
    warningTransparent: {
      light: 'rgba(235,218,115,0.2)',
      main: 'rgba(235,205,37,0.2)',
      dark: 'rgba(235,164,6,0.2)',
    },
    urgent: {
      light: '#ffd5c1',
      main: '#c34304',
      dark: '#a33600',
    },
    urgentTransparent: {
      light: 'rgba(255,213,193,0.2)',
      main: 'rgba(195,67,4,0.2)',
      dark: 'rgba(163,54,0,0.2)',
    },
    white: '#FFFFFF',
    completed: {
      light: '#daffd6',
      main: '#21d713',
      dark: '#28ac1d',
    },
    studyGuide: {
      light: '#f1f6e0',
      main: '#bad389',
      dark: '#9aba59',
    },
    studentProgress: {
      high: '#1E854D',
      medium: '#FFB800',
      low: '#952222',
      completed: '#129FBE',
      uncompleted: '#EBE6DC',
    },
    studyCard: {
      accessibleBlue: '#006B8A',
      green: '#1E854D',
      grey: '#EAE6DC',
    },
    trajectory: {
      draft: '#7B766A',
    },
    genevaGreen: '#1B7D78',
  },
  typography: {
    htmlFontSize: 10,
    fontFamily: primaryFontFamily,
    h1: {
      fontSize: 'clamp(2.375rem, 1.155vw + 2.076rem, 3rem)',
      fontWeight: 700,
      color: '#2D2A28',
    },
    h2: {
      fontSize: 'clamp(1.813rem, 0.808vw + 1.603rem, 2.25rem)',
      fontWeight: 700,
    },
    h3: {
      fontSize: 'clamp(1.438rem, 0.693vw + 1.258rem, 1.813rem)',
      fontWeight: 700,
    },
    h4: {
      fontSize: 'clamp(1.188rem, 0.577vw + 1.038rem, 1.4rem)',
      fontWeight: 700,
    },
    h5: {
      fontSize: 'clamp(0.938rem, 0.462vw + 0.818rem, 1.188rem)',
      fontWeight: 700,
    },
    body1: {
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: 1.1,
    },
    body2: {
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: 1.1,
    },
    bodyXs: {
      fontSize: 'clamp(0.8rem, 0.462vw + 0.818rem, 0.9rem)',
      fontWeight: 400,
      lineHeight: 1.1,
    },
    bodySm: {
      fontSize: 'clamp(0.9rem, 0.462vw + 0.818rem, 1rem)',
      fontWeight: 400,
      lineHeight: 1.1,
    },
    bodyMd: {
      fontSize: 'clamp(1rem, 0.462vw + 0.818rem, 1.1rem)',
      fontWeight: 400,
      lineHeight: 1.1,
    },
    bodyLg: {
      fontSize: 'clamp(1.1rem, 0.462vw + 0.818rem, 1.2rem)',
      fontWeight: 400,
      lineHeight: 1.1,
    },
    subtitle1: {
      fontFamily: secondaryFontFamily,
      fontStyle: 'light',
      fontSize: '1.1rem',
    },
  },
  overrides: {
    MuiAlert: {
      standardInfo: {
        backgroundColor: '#E5F6FD',
        color: '#014361',
      },
      standardWarning: {
        backgroundColor: '#EC6523',
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: defaultBorderRadius,
      },
      elevation1: {
        boxShadow: defaultBoxShadow,
      },
    },
    MuiTouchRipple: {
      root: {
        opacity: '.4',
      },
    },
    MuiDialogTitle: {
      root: {
        fontSize: '2.2rem',
        fontWeight: 700,
      },
    },
    MuiDialogContent: {
      root: {
        overflowX: 'hidden',
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: '16px',
        padding: '16px 12px',
      },
    },
    MuiBadge: {
      badge: {
        minWidth: 'unset',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
      },
      colorSecondary: {
        color: '#EC6523',
        backgroundColor: '#ffffff',
        border: '1.5px solid #EC6523',
        boxSizing: 'border-box',
      },
    },
  },
  props: {
    MuiPaper: {
      square: true,
    },
    MuiPopover: {
      elevation: 1,
    },
    MuiDialogTitle: {
      disableTypography: true,
    },
    MuiTooltip: {
      placement: 'left',
    },
    MuiAccordion: {
      square: true,
    },
  },
  config: {
    defaultBorder,
    defaultBorderRadius,
    borderRadiusCluster,
    defaultBoxShadow,
    defaultTransition,
    primaryFontFamily,
    secondaryFontFamily,
    appbarHeight,
    subHeaderHeight,
    bottomBarHeight,
    bottomBarHeightPx,
    appbarHeightPx,
    subHeaderHeightPx,
    trajectoryToolboxWidth,
    messageBarHeight,
    messageBarHeightPx: messageBarHeightPx,
    sidePanelScroolWidthRem: sidePanelScroolWidthRem,
    openNavigationBarWidth: openNavigationBarWidth,
    openNavigationBarWidthPx: openNavigationBarWidthPx,
    closedNavigationBarWidth: closedNavigationBarWidth,
    closedNavigationBarWidthPx: closedNavigationBarWidthPx,
    mobileMessageBarHeight,
    progressColors: {
      bronze: {
        main: '#edc0a1',
        hover: '#e6ab82',
        icon: '#af6431',
      },
      silver: {
        main: '#dde2e6',
        hover: '#bcc7cf',
        icon: '#82898f',
      },
      gold: {
        main: '#f2da5c',
        hover: '#e6cb3f',
        icon: '#b08413',
      },
    },
    visibilityColors: {
      visible: {
        main: '#fff',
        hover: '#bcc7cf',
        icon: '#EC6523',
      },
      notVisible: {
        main: '#fff',
        hover: '#bcc7cf',
        icon: '#82898f',
      },
    },
    // TODO - 1610
    appbarColors: {
      background: '#F3F1EC',
      text: '#F3F1EC',
      icon: '#2D2A28',
      borderColor: '#EAE6DC',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme;
